import React from "react";
import { MapPin, PencilSimple, Trash } from "@phosphor-icons/react";

import { Empty, LoadingBlock } from "../../../design-system/components/desktop";
import {
  Panel,
  PanelAction,
  PanelBadge,
  PanelRow,
  PanelRowAction,
  PanelRows,
} from "../../../design-system/patterns/settings";

const formatAddress = (address) =>
  [
    address.address_line1,
    address.address_line2,
    address.city,
    address.province,
    address.postal_code,
  ]
    .filter(Boolean)
    .join(", ");

/**
 * Saved addresses, the CRUD list. Pure: the queries and the mutations moved up
 * to `Profile.jsx` so this file can be mounted with fixture data.
 *
 * DELETE IS A GHOST ICON PLUS A REAL CONFIRMATION, never a red button. There is
 * no `.dsk-btn--danger` in this system and none is invented: the consequence
 * lives in the confirmation's own label ("Delete address"), which the container
 * renders through `ConfirmForm`.
 *
 * The default address has no delete control at all, which is the existing
 * behaviour and the right one: deleting the default would leave the account
 * with no address and no way to say which one takes its place.
 *
 * THE STORE ADDRESS IS LOCKED HERE, and the API refuses it too. Couriers collect
 * from that row, so it changes only in the labelled store section, where the
 * consequence is stated. It still lists because checkout can deliver to it.
 */
const SavedAddresses = ({
  addresses = [],
  isLoading = false,
  onAdd,
  onEdit,
  onDelete,
}) => (
  <Panel
    icon={MapPin}
    title="Saved addresses"
    sub={
      addresses.some((address) => address.is_store_address)
        ? "Default is what checkout fills in. It doesn't change where couriers collect from."
        : undefined
    }
    action={<PanelAction onClick={onAdd}>Add address</PanelAction>}
  >
    {isLoading ? (
      <LoadingBlock>Loading your addresses</LoadingBlock>
    ) : addresses.length === 0 ? (
      <Empty
        icon={MapPin}
        illustration="no-addresses-yet"
        title="No saved addresses yet"
        text="Add one so checkout can fill itself in."
        inline
        actions={<PanelAction onClick={onAdd}>Add your first address</PanelAction>}
      />
    ) : (
      <PanelRows>
        {addresses.map((address) => (
          <PanelRow
            key={address.id}
            title={address.title || "Address"}
            badge={
              address.is_store_address || address.is_default ? (
                <>
                  {address.is_store_address ? <PanelBadge>Store address</PanelBadge> : null}
                  {address.is_default ? <PanelBadge>Default</PanelBadge> : null}
                </>
              ) : null
            }
            detail={formatAddress(address)}
            actions={
              address.is_store_address ? null : (
                <>
                  <PanelRowAction
                    icon={PencilSimple}
                    label={`Edit ${address.title || "address"}`}
                    onClick={() => onEdit(address)}
                  />
                  {!address.is_default ? (
                    <PanelRowAction
                      icon={Trash}
                      label={`Delete ${address.title || "address"}`}
                      onClick={() => onDelete(address)}
                    />
                  ) : null}
                </>
              )
            }
          />
        ))}
      </PanelRows>
    )}
  </Panel>
);

export default SavedAddresses;
